import React, { useState, useEffect } from 'react';
import { ArrowLeft, ShoppingCart, Gift, Sparkles, RotateCcw, Package } from 'lucide-react';
import axios from 'axios';
import ProductCard from './ProductCard';
import CartSidebar from './CartSidebar';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import DeliveryModal from './DeliveryModal';
import ProductDetailModal from './ProductDetailModal';
import CombosModal from './CombosModal';

const API_URL = process.env.REACT_APP_API_URL;

function ProviderCatalog({ userId, setActiveComponent }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCombosModal, setShowCombosModal] = useState(false);
  const [combosCount, setCombosCount] = useState(0);
  
  // NEW: Provider filtering state
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedProviderName, setSelectedProviderName] = useState('');

  useEffect(() => {
    fetchProducts(currentPage, searchQuery);
    fetchCombosCount();
  }, [currentPage, selectedProviderId]); // Add selectedProviderId to dependencies

  async function fetchProducts(page = 1, search = '') {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page,
        per_page: 12,
        search: search || undefined
      };
      
      // NEW: Add provider filter if selected
      if (selectedProviderId) {
        params.provider_id = selectedProviderId;
      }
      
      const response = await axios.get(`${API_URL}/api/catalog/products`, { params });

      setProducts(response.data.products);
      setTotalPages(response.data.pages);
      setCurrentPage(response.data.current_page);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Error al cargar los productos. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchCombosCount() {
    try {
      const response = await axios.get(`${API_URL}/api/combos/count`);
      setCombosCount(response.data.count);
    } catch (err) {
      console.error('Error fetching combos count:', err);
      setCombosCount(0);
    }
  }

  function handleSearch(query) {
    setSearchQuery(query);
    setCurrentPage(1);
    fetchProducts(1, query);
  }

  function handleClearSearch() {
    setSearchQuery('');
    setCurrentPage(1);
    fetchProducts(1, '');
  }

  function handleAddToCart(product, quantity = 1) {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.sku === product.sku);
      
      // NEW: Set provider filter when first item is added
      if (prevCart.length === 0 && product.seller) {
        setSelectedProviderId(product.seller.id);
        setSelectedProviderName(product.seller.username);
        setCurrentPage(1);
        // REMOVED: setTimeout(() => fetchProducts(1, searchQuery), 0);
        // The useEffect will handle the refetch automatically
      }
      
      if (existingItem) {
        return prevCart.map(item =>
          item.sku === product.sku
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      return [...prevCart, { ...product, quantity }];
    });
  }

  function handleUpdateCartQuantity(sku, newQuantity) {
    if (newQuantity <= 0) {
      handleRemoveFromCart(sku);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.sku === sku ? { ...item, quantity: newQuantity } : item
      )
    );
  }

  function handleRemoveFromCart(sku) {
    setCart(prevCart => {
      const newCart = prevCart.filter(item => item.sku !== sku);
      
      // NEW: Clear provider filter when cart becomes empty
      if (newCart.length === 0) {
        setSelectedProviderId(null);
        setSelectedProviderName('');
        setCurrentPage(1);
        // REMOVED: setTimeout(() => fetchProducts(1, searchQuery), 0);
        // The useEffect will handle the refetch automatically
      }
      
      return newCart;
    });
  }
  // NEW: Handle changing provider (clears cart)
  function handleChangeProvider() {
    const confirmChange = window.confirm(
      '¿Estás seguro? Esto vaciará tu carrito actual y podrás seleccionar productos de otro proveedor.'
    );
    
    if (confirmChange) {
      setCart([]);
      setSelectedProviderId(null);
      setSelectedProviderName('');
      setCurrentPage(1);
      setSearchQuery('');
      fetchProducts(1, '');
    }
  }

  function handleOpenDeliveryModal() {
    if (cart.length === 0) {
      alert('Agrega productos al carrito primero');
      return;
    }
    
    // Check if all products are from the same provider
    const providerIds = [...new Set(cart.map(item => item.seller?.id).filter(Boolean))];
    
    if (providerIds.length > 1) {
      alert('Solo puedes ordenar productos de un proveedor a la vez. Por favor vacía el carrito y selecciona productos de un solo proveedor.');
      return;
    }
    
    setShowDeliveryModal(true);
  }

  function handleCloseDeliveryModal() {
    setShowDeliveryModal(false);
  }

  function handleDeliverySuccess() {
    setCart([]);
    setSelectedProviderId(null);
    setSelectedProviderName('');
    setShowDeliveryModal(false);
    setCurrentPage(1);
    fetchProducts(1, searchQuery);
    alert('¡Entrega registrada exitosamente!');
  }

  function handleCardClick(product) {
    setSelectedProduct(product);
  }

  function handleCloseProductModal() {
    setSelectedProduct(null);
  }

  function handleAddToCartFromModal(product, quantity) {
    handleAddToCart(product, quantity);
  }

  function handleOpenCombosModal() {
    setShowCombosModal(true);
  }

  function handleCloseCombosModal() {
    setShowCombosModal(false);
    fetchCombosCount();
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setActiveComponent('Options')}
              className="flex items-center gap-2 text-slate-700 hover:text-brand-primary transition-colors font-montserrat"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Volver</span>
            </button>

            <h1 className="text-2xl font-bold text-brand-primary font-montserrat">
              Catálogo FAST MAX
            </h1>

            {/* Combos Button */}
            <button
              onClick={handleOpenCombosModal}
              className="relative group"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary via-brand-gold to-brand-green rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-300 animate-pulse" />
              <div className="relative flex items-center gap-2 bg-gradient-to-r from-brand-primary to-brand-gold text-white px-4 py-2 rounded-lg font-montserrat font-semibold shadow-lg hover:shadow-xl transition-all">
                <Gift className="w-5 h-5 animate-bounce" />
                <span className="hidden sm:inline">Combos</span>
                <Sparkles className="w-4 h-4" />
                {combosCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full border-2 border-white">
                    {combosCount}
                  </span>
                )}
              </div>
            </button>
          </div>

          <SearchBar
            searchQuery={searchQuery}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          {/* NEW: Provider Filter Banner */}
          {selectedProviderId && (
            <div className="mt-4 bg-gradient-to-r from-brand-primary to-brand-primary/90 text-white rounded-lg p-4 shadow-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5" />
                  <div>
                    <p className="font-semibold font-montserrat text-sm">
                      Mostrando productos de:
                    </p>
                    <p className="font-bold font-montserrat text-lg">
                      {selectedProviderName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleChangeProvider}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-all font-montserrat font-semibold text-sm"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Cambiar Proveedor</span>
                  <span className="sm:hidden">Cambiar</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        {/* Products Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 font-montserrat">
              {error}
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-slate-500 font-montserrat text-lg">
                {searchQuery
                  ? `No se encontraron productos para "${searchQuery}"`
                  : selectedProviderId
                  ? `No hay más productos disponibles de ${selectedProviderName}`
                  : 'No hay productos disponibles'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onCardClick={handleCardClick}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>

        {/* Cart Sidebar */}
        <CartSidebar
          cart={cart}
          onUpdateQuantity={handleUpdateCartQuantity}
          onRemove={handleRemoveFromCart}
          onCheckout={handleOpenDeliveryModal}
          selectedProviderName={selectedProviderName}
          onChangeProvider={selectedProviderId ? handleChangeProvider : null}
        />
      </div>

      {/* Delivery Modal */}
      {showDeliveryModal && (
        <DeliveryModal
          userId={userId}
          cart={cart}
          providerId={cart[0]?.seller?.id || null}
          onClose={handleCloseDeliveryModal}
          onSuccess={handleDeliverySuccess}
        />
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={handleCloseProductModal}
          onAddToCart={handleAddToCartFromModal}
        />
      )}

      {/* Combos Modal */}
      {showCombosModal && (
        <CombosModal onClose={handleCloseCombosModal} />
      )}
    </div>
  );
}

export default ProviderCatalog;